import { Box, PoundIcon, Radio, CurrencyInput, useBrand, BRANDS } from '@cof/plastic-components';
import { withText } from '../../../text/textStore';
import { useFormContext, Controller } from 'react-hook-form';
import { GridRadioGroup, FullInputWidth, InputWrapper } from './SharedStyledComponents';
import { useContext, useState } from 'react';
import ErrorHandler, { shouldDisplayFormTick, shouldDisplayRadioTick } from './ErrorHandler';
import { DescriptionSection, LabelSection } from '../../../components/TextField/TextField';
import { autoCompleteMapping, FORM_SECTIONS } from './FormIDs';
import { FormUtilsContext } from '../QuickCheck';
import Group from '../../../components/Group';
import PropTypes from 'prop-types';
import { NarrativeTextHookForm } from '../../../components/NarrativeText/NarrativeText';
import OtherIncomeInput from './OtherIncomeInput';
import { getAriaDescribedBy } from '../../../utilities/utils';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';

const employmentStatuses = {
    'Employed': 'EM',
    'Self-employed': 'SE',
    'Student': 'ST',
    'Retired': 'RE',
    'Homemaker': 'HO',
    'Unemployed': 'UN'
};

const EmploymentForm = ({ Text, Text: text, JsxText }: any) => {
    const {
        register,
        formState: { errors, touchedFields },
        watch,
        getValues
    } = useFormContext();

    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    const brand = useBrand();
    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    const employmentIDs = FORM_SECTIONS.Employment;
    const [hasYearlyIncomeFocused, setHasYearlyIncomeFocused] = useState(false);

    const isASOS = brand === BRANDS.ASOS.toLowerCase();

    watch(employmentIDs.Occupation);
    watch(employmentIDs.YearlyIncome);

    return (
        <>
            <Box>
                <NarrativeTextHookForm data-qm-allow>
                    <Text path="Heading" />
                </NarrativeTextHookForm>

                <GridRadioGroup
                    id={employmentIDs.EmploymentStatus}
                    name={employmentIDs.EmploymentStatus}
                    variant="square"
                    optionWrapperProps={{
                        width: 'auto',
                        // @ts-expect-error TS2769: No overload matches this call.
                        margin: '0'
                    }}
                    aria-describedby={getAriaDescribedBy(employmentIDs.EmploymentStatus, submitAttempted, [], errors)}
                >
                    <LabelSection
                        id={employmentIDs.EmploymentStatus}
                        label={<Text path="EmploymentStatus.Heading"></Text>}
                        py="xs"
                        as="legend"
                        // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                        displayTick={shouldDisplayRadioTick(employmentIDs.EmploymentStatus, watch, errors)}
                        data-qm-allow
                    />
                    {Object.entries(employmentStatuses).map(([displayName, value]) => (
                        <Radio
                            key={`title-button-${value}`}
                            {...register(employmentIDs.EmploymentStatus, {
                                required: 'Please select an option for Employment status'
                            })}
                            value={value}
                            // @ts-expect-error TS2322: Type ' children: string; value: string; margin: string; "data-qm-allow": true; onChange: ChangeHandl
                            margin="0"
                            data-qm-allow
                        >
                            {displayName}
                        </Radio>
                    ))}
                </GridRadioGroup>
                <ErrorHandler
                    id={employmentIDs.EmploymentStatus}
                    show={touchedFields[employmentIDs.EmploymentStatus]}
                    data-qm-allow
                />

                <Group>
                    <InputWrapper>
                        <LabelSection
                            id={employmentIDs.Occupation}
                            label={<Text path="Occupation"></Text>}
                            displayTick={shouldDisplayFormTick(employmentIDs.Occupation, getValues, errors)}
                            pt="xs"
                            data-qm-allow
                        />
                        {isASOS && (
                            <DescriptionSection
                                description="Skip this Q if you don't have a job right now..."
                                id={employmentIDs.Occupation}
                            />
                        )}
                        <FullInputWidth
                            id={employmentIDs.Occupation}
                            data-qa-id={employmentIDs.Occupation}
                            {...register(employmentIDs.Occupation, {
                                pattern: { value: /^[0-9a-zA-Z'`,.& -]+$/, message: 'This is not a valid occupation' },
                                maxLength: { value: 32, message: 'Occupation must be 32 characters or less' }
                            })}
                            variant={variantTernary(employmentIDs.Occupation)}
                            aria-describedby={getAriaDescribedBy(
                                employmentIDs.Occupation,
                                submitAttempted,
                                isASOS ? [`${employmentIDs.Occupation}-helper-text`] : undefined,
                                errors
                            )}
                            autoComplete={autoCompleteMapping['occupation']}
                        />
                        <ErrorHandler
                            id={employmentIDs.Occupation}
                            show={touchedFields[employmentIDs.Occupation]}
                            data-qm-allow
                        />
                    </InputWrapper>
                </Group>

                <Group>
                    <InputWrapper data-qa-id={`${employmentIDs.YearlyIncome}-input-wrapper`}>
                        <LabelSection
                            id={employmentIDs.YearlyIncome}
                            label={<Text path="YearlyIncome.Heading"></Text>}
                            displayTick={shouldDisplayFormTick(employmentIDs.YearlyIncome, getValues, errors)}
                            pt="xs"
                            data-qm-allow
                        />

                        <DescriptionSection
                            description={text({ path: 'YearlyIncome.Description' })}
                            id={employmentIDs.YearlyIncome}
                        />
                        <Controller
                            name={employmentIDs.YearlyIncome}
                            rules={{
                                required: 'Yearly income is required',
                                maxLength: { value: 7, message: 'Yearly income must be a max of 7 digits' }
                            }}
                            render={({ field }: any) => {
                                const additionalIDs = [`${employmentIDs.YearlyIncome}-helper-text`];
                                if (hasYearlyIncomeFocused) {
                                    additionalIDs.push(`${employmentIDs.YearlyIncome}-explanation-box-text`);
                                }
                                return (
                                    <CurrencyInput
                                        id={employmentIDs.YearlyIncome}
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onValueChange={(value: any) => {
                                            field.onChange(value ?? '');
                                        }}
                                        onFocus={() => setHasYearlyIncomeFocused(true)}
                                        onBlur={field.onBlur}
                                        data-qa-id={employmentIDs.YearlyIncome}
                                        allowDecimals={false}
                                        allowNegativeValue={false}
                                        explanation={{
                                            Icon: PoundIcon,
                                            body: (
                                                <span id={`${employmentIDs.YearlyIncome}-explanation-box-text`}>
                                                    <JsxText
                                                        path="YearlyIncome.Explanation"
                                                        params={{
                                                            BOLD_TEXT: <b>go down</b>
                                                        }}
                                                    ></JsxText>
                                                </span>
                                            ),
                                            textId: 'explanation-text',
                                            prefix: employmentIDs.YearlyIncome
                                        }}
                                        variant={variantTernary(employmentIDs.YearlyIncome)}
                                        aria-describedby={getAriaDescribedBy(
                                            employmentIDs.YearlyIncome,
                                            submitAttempted,
                                            additionalIDs,
                                            errors
                                        )}
                                    />
                                );
                            }}
                        />
                        <ErrorHandler
                            id={employmentIDs.YearlyIncome}
                            show={touchedFields[employmentIDs.YearlyIncome]}
                            data-qm-allow
                        />
                    </InputWrapper>
                </Group>
                <OtherIncomeInput />
            </Box>
        </>
    );
};

EmploymentForm.propTypes = {
    Text: PropTypes.func.isRequired,
    JsxText: PropTypes.func.isRequired
};

export default withText('Employment')(EmploymentForm);
